import React, { useState, useEffect, useRef } from "react";
import { fixPhotoUrl } from "../../lib/utils";

// In-memory cache for blob URLs so we don't re-fetch images on every render
const blobCache = new Map();

/**
 * SafeImage Component
 * Automatically resolves relative & backend URLs, bypasses ngrok browser interstitial warnings,
 * falls back gracefully with custom placeholders or initials, and caches blobs for high performance.
 */
export const SafeImage = ({
  src,
  alt = "",
  className = "",
  fallback = null,
  onClick,
  style = {},
  loading = "lazy",
  ...props
}) => {
  const [imgSrc, setImgSrc] = useState(null);
  const [hasError, setHasError] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    setHasError(false);
    setIsLoaded(false);

    const resolvedUrl = fixPhotoUrl(src);
    if (!resolvedUrl) {
      setImgSrc(null);
      setHasError(true);
      return;
    }

    // Check if we already have a cached blob URL
    if (blobCache.has(resolvedUrl)) {
      setImgSrc(blobCache.get(resolvedUrl));
      return;
    }

    // If the URL is an ngrok URL and not running on localhost rewrite, fetch with skip header
    const isNgrok = resolvedUrl.includes("ngrok");

    if (isNgrok) {
      fetch(resolvedUrl, {
        headers: {
          "ngrok-skip-browser-warning": "69420",
          Accept: "image/*",
        },
      })
        .then((res) => {
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          return res.blob();
        })
        .then((blob) => {
          if (!isMounted.current) return;
          const blobUrl = URL.createObjectURL(blob);
          blobCache.set(resolvedUrl, blobUrl);
          setImgSrc(blobUrl);
        })
        .catch(() => {
          if (!isMounted.current) return;
          // If fetch fails, try the resolved URL directly as fallback
          setImgSrc(resolvedUrl);
        });
    } else {
      setImgSrc(resolvedUrl);
    }
  }, [src]);

  const handleImageError = () => {
    const resolvedUrl = fixPhotoUrl(src);
    // If direct load failed on a URL that wasn't fetched yet, try fetching as blob with bypass header
    if (resolvedUrl && !blobCache.has(resolvedUrl)) {
      fetch(resolvedUrl, {
        headers: {
          "ngrok-skip-browser-warning": "69420",
          Accept: "image/*",
        },
      })
        .then((res) => {
          if (!res.ok) throw new Error("Fetch failed");
          return res.blob();
        })
        .then((blob) => {
          if (!isMounted.current) return;
          const blobUrl = URL.createObjectURL(blob);
          blobCache.set(resolvedUrl, blobUrl);
          setImgSrc(blobUrl);
        })
        .catch(() => {
          if (isMounted.current) {
            setHasError(true);
          }
        });
    } else {
      setHasError(true);
    }
  };

  if (hasError || !imgSrc) {
    if (fallback) return <>{fallback}</>;
    return (
      <div
        className={`flex items-center justify-center bg-gray-100 text-gray-400 text-xs ${className}`}
        style={style}
        onClick={onClick}
      >
        <span className="truncate px-1">{alt ? alt.charAt(0).toUpperCase() : "?"}</span>
      </div>
    );
  }

  return (
    <img
      src={imgSrc}
      alt={alt}
      className={`${className} ${!isLoaded ? "opacity-90" : "opacity-100"} transition-opacity duration-200`}
      style={style}
      loading={loading}
      onClick={onClick}
      onLoad={() => setIsLoaded(true)}
      onError={handleImageError}
      {...props}
    />
  );
};

export default SafeImage;
